import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as charts from 'echarts';
import { useTheme } from '../../../contexts/ThemeContext';
import { useMobile } from '../../../contexts/MobileContext';
import TimeSelector from './TimeSelector';
import useChartAutoResize from '../charting/useChartAutoResize';
import {
  getAxisChartGrid,
  getFadedLineStroke,
  getChartGraphicLabel,
  getChartSemanticColors,
  getChartTheme,
  getChartTooltipMarker,
  getChartTooltipStyle,
  getChartTypography,
} from '../charting/chartTheme';

const getMinutesAxis = (minutes) => {
  const values = [...Array(minutes).keys()].map((index) => -(minutes - index));
  values.push('');
  return values;
};

const getGlowMarkPoint = (value, colors, minutes) => ({
  symbolKeepAspect: true,
  data: [
    {
      coord: [minutes - 1, value],
      symbol: 'circle',
      symbolSize: 30,
      itemStyle: {
        color: new charts.graphic.RadialGradient(0.5, 0.5, 0.5, [
          { offset: 0, color: colors[0] },
          { offset: 1, color: 'rgba(0, 0, 0, 0)' },
        ]),
      },
    },
    {
      coord: [minutes - 1, value],
      symbol: 'circle',
      symbolSize: 10,
      itemStyle: {
        color: colors[0],
      },
    },
    {
      coord: [minutes - 1, value],
      symbol: 'circle',
      symbolSize: 6,
      itemStyle: {
        color: colors[1],
      },
    },
  ],
});

const getOption = (seriesData, selectedTime, theme, isMobile) => {
  const colors = getChartSemanticColors(theme);
  const typography = getChartTypography(isMobile);
  const minutes = selectedTime === '1h' ? 60 : parseInt(selectedTime.slice(0, -1), 10);
  const humanData = seriesData.map((item) => item.human).slice(-minutes);
  const botData = seriesData.map((item) => item.bot).slice(-minutes);

  return {
    tooltip: getChartTooltipStyle(theme, typography, {
      confine: true,
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: (params) => {
        const sorted = [...params].sort((a, b) => {
          if (a.seriesName === 'Human') return -1;
          if (b.seriesName === 'Human') return 1;
          return 0;
        });

        return sorted.map((item) => (
          `<div>
            ${getChartTooltipMarker(item.seriesName === 'Human' ? colors.human : colors.bot)}
            ${item.seriesName}: ${item.value}
          </div>`
        )).join('');
      },
    }),
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: getMinutesAxis(minutes),
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textMid,
        interval: isMobile ? Math.max(1, Math.floor(minutes / 3)) : Math.max(1, Math.floor(minutes / 6)),
        fontSize: typography.axisLabelSize,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitNumber: isMobile ? 3 : 4,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: theme.textHigh,
        fontSize: typography.axisLabelSize,
      },
      splitLine: {
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
    },
    grid: getAxisChartGrid(isMobile, { hasLegend: false, top: isMobile ? '16%' : '14%' }),
    graphic: [getChartGraphicLabel(theme, isMobile, 'Clicks')],
    series: [
      {
        name: 'Bot',
        type: 'line',
        data: botData,
        smooth: false,
        showSymbol: false,
        color: colors.bot,
        itemStyle: {
          color: colors.bot,
          borderColor: theme.surfaceLow,
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            color: colors.bot,
            borderColor: theme.surfaceLow,
            borderWidth: 1,
          },
        },
        lineStyle: {
          width: isMobile ? 1.25 : 1.75,
          color: getFadedLineStroke(charts, colors.bot),
        },
        markPoint: getGlowMarkPoint(
          botData[botData.length - 1] ?? 0,
          [colors.bot, theme.surfaceLow],
          minutes
        ),
      },
      {
        name: 'Human',
        type: 'line',
        data: humanData,
        smooth: false,
        showSymbol: false,
        color: colors.human,
        itemStyle: {
          color: colors.human,
          borderColor: theme.surfaceLow,
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            color: colors.human,
            borderColor: theme.surfaceLow,
            borderWidth: 1,
          },
        },
        lineStyle: {
          width: isMobile ? 1.25 : 1.75,
          color: getFadedLineStroke(charts, colors.human),
        },
        markPoint: getGlowMarkPoint(
          humanData[humanData.length - 1] ?? 0,
          [colors.human, theme.surfaceLow],
          minutes
        ),
      },
    ],
    backgroundColor: 'transparent',
  };
};

const LiveBotTrackingGraph = ({ site, setReady }) => {
  const { isDarkMode } = useTheme();
  const { isMobile } = useMobile();
  const chartRef = useRef(null);
  const [data, setData] = useState(null);
  const theme = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const times = useMemo(() => ['1h', '30m', '20m', '10m'], []);
  const [selected, setSelected] = useState(times[0]);

  const updateData = useCallback(() => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-live-data', {
      method: 'POST',
      headers: {
        Authorization: localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ website: site.website }),
    })
      .then((response) => response.json())
      .then((payload) => {
        if (payload.success) {
          setData(payload.data);
        }
      });
  }, [site.website]);

  useEffect(() => {
    updateData();
  }, [updateData]);

  useEffect(() => {
    const timer = setInterval(() => updateData(), 60000);
    return () => clearInterval(timer);
  }, [updateData]);

  useChartAutoResize(chartRef, [isDarkMode, isMobile, selected, data]);

  const chartOption = data ? getOption(data, selected, theme, isMobile) : null;

  const handleChartReady = (chart) => {
    chart.off('finished');
    chart.on('finished', () => {
      setReady('live');
    });
  };

  return (
    <div className="live-bot-tracking">
      <div className="traffic-graph__tab-header">
        <div className="chart-title">Live Bot Tracking</div>
        <TimeSelector times={times} selected={selected} setSelected={setSelected} />
      </div>

      <div className="chart-wrap site-overview-graph-shell site-overview-graph-shell--live">
        {chartOption && (
          <ReactECharts
            ref={chartRef}
            option={chartOption}
            opts={{ renderer: isMobile ? 'svg' : 'canvas' }}
            notMerge={true}
            onChartReady={handleChartReady}
            key={`${isDarkMode ? 'dark' : 'light'}-${isMobile ? 'm' : 'd'}-${selected}`}
            className="chart site-overview-chart-canvas"
          />
        )}
      </div>

      <div className="traffic-graph__summary">
        <div className="traffic-graph__summary-item">
          <span className="chart-label__swatch chart-label__swatch--human" />
          <span className="traffic-graph__summary-label">Human</span>
        </div>
        <div className="traffic-graph__summary-item">
          <span className="chart-label__swatch chart-label__swatch--bot" />
          <span className="traffic-graph__summary-label">Bot</span>
        </div>
      </div>
    </div>
  );
};

export default LiveBotTrackingGraph;
